import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  useToast,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Select,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Link,
  Divider,
  Progress,
  Tooltip,
  SimpleGrid,
  Stack,
  Switch,
  List,
  ListItem,
  ListIcon,
  Checkbox,
  Icon,
} from "@chakra-ui/react";
import { SettingsIcon, DeleteIcon, EditIcon, ViewIcon, ExternalLinkIcon, CheckIcon, CloseIcon, AddIcon } from "@chakra-ui/icons";
import { FaCheckCircle, FaTimesCircle, FaRocket, FaBolt, FaCrown, FaFire, FaTicketAlt } from "react-icons/fa";
import { BsStars } from "react-icons/bs";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../services/firebaseService";
import { useNavigate } from "react-router-dom";
import { useImpersonation } from "../context/ImpersonationContext";

interface UserData {
  id: string;
  email: string;
  role?: string;
  credits?: number;
  plan?: string;
  createdAt?: any;
  authCreationTime?: string;
  lastSignInTime?: string;
  companyName?: string;
  projectCount?: number;
}

interface Coupon {
    id: string;
    code: string;
    credits: number;
    plan?: string;
    repeatable: boolean;
    createdAt?: string;
}

function getAmountFromStripeData(
  stripePricesData: { config?: Record<string, string>; prices?: { id: string; unit_amount?: number }[] } | null,
  planKey: string
): number | null {
  if (!stripePricesData?.config?.[planKey]) return null;
  const priceId = stripePricesData.config[planKey];
  const p = stripePricesData.prices?.find((x) => x.id === priceId);
  if (!p?.unit_amount) return null;
  return p.unit_amount / 100;
}

function AdminPricingPreview({
  stripePricesData,
  isAnnual,
  t,
  onPriceUpdate,
  updatingPlanKey,
}: {
  stripePricesData: { config?: Record<string, string>; prices?: { id: string; unit_amount?: number }[] } | null;
  isAnnual: boolean;
  t: (k: string) => string;
  onPriceUpdate: (planKey: string, amountEuros: number) => Promise<void>;
  updatingPlanKey: string | null;
}) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const handleStartEdit = (planKey: string, currentAmount: number) => {
    setEditingKey(planKey);
    setEditValue(currentAmount.toString());
  };

  const handleSaveEdit = async () => {
    if (!editingKey) return;
    const parsed = parseFloat(editValue.replace(",", "."));
    if (!isNaN(parsed) && parsed >= 0) {
      await onPriceUpdate(editingKey, parsed);
    }
    setEditingKey(null);
    setEditValue("");
  };

  const EditablePrice = ({
    planKey,
    amount,
    fontSize = "4xl",
  }: {
    planKey: string;
    amount: number;
    fontSize?: string;
  }) => {
    const isEditing = editingKey === planKey;
    const isUpdating = updatingPlanKey === planKey;
    return (
      <Box
        as="span"
        cursor="pointer"
        _hover={{ bg: "whiteAlpha.100", borderRadius: "md", px: 1 }}
        onClick={() => !isUpdating && handleStartEdit(planKey, amount)}
        title={t("adminClickToEdit")}
      >
        {isEditing ? (
          <Flex as="span" align="baseline" gap={1}>
            <Input
              size="sm"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveEdit();
                if (e.key === "Escape") {
                  setEditingKey(null);
                  setEditValue("");
                }
              }}
              w="70px"
              autoFocus
              type="number"
              min={0}
              step={0.01}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
            <Text as="span" fontSize={fontSize} fontWeight="bold" color="white">
              €
            </Text>
          </Flex>
        ) : (
          <Text as="span" fontSize={fontSize} fontWeight="bold" color="white">
            {isUpdating ? "…" : Math.round(amount)}€
          </Text>
        )}
      </Box>
    );
  };
  const starterMonthly = getAmountFromStripeData(stripePricesData, "starter_monthly") ?? 29;
  const starterAnnual = getAmountFromStripeData(stripePricesData, "starter_annual") ?? 19;
  const proMonthly = getAmountFromStripeData(stripePricesData, "pro_monthly") ?? 79;
  const proAnnual = getAmountFromStripeData(stripePricesData, "pro_annual") ?? 49;
  const empireMonthly = getAmountFromStripeData(stripePricesData, "empire_monthly") ?? 149;
  const empireAnnual = getAmountFromStripeData(stripePricesData, "empire_annual") ?? 99;
  const payAsYouGo = getAmountFromStripeData(stripePricesData, "pay-as-you-go") ?? 29;

  const highlightNeon = "teal.400";
  const accentColor = "teal.500";
  const primaryColor = "blue.400";

  const cardProps = [
    {
      title: "Débutant",
      description: "Pour lancer votre premier Bestseller.",
      price: isAnnual ? starterAnnual : starterMonthly,
      annualPrice: starterMonthly,
      planKeyMain: isAnnual ? "starter_annual" : "starter_monthly",
      planKeyCrossed: "starter_monthly",
      icon: FaRocket,
      accentColor: primaryColor,
      features: [
        { text: "150 Crédits / mois", bold: true, check: true },
        { text: "Génération Listing complet (Titres, Bullets)", check: true },
        { text: "Recherche de Mots-clés basique", check: true },
        { text: "Analyse Photo Standard", check: true },
        { text: "Support Email", check: true },
        { text: "Monkey Roast", check: false },
        { text: "Photo Insights Pro", check: false },
      ],
    },
    {
      title: "Confirmé",
      description: "Dominez votre niche et optimisez en continu.",
      price: isAnnual ? proAnnual : proMonthly,
      annualPrice: proMonthly,
      planKeyMain: isAnnual ? "pro_annual" : "pro_monthly",
      planKeyCrossed: "pro_monthly",
      icon: FaBolt,
      accentColor: highlightNeon,
      isPopular: true,
      features: [
        { text: "600 Crédits / mois", bold: true, check: true },
        { text: "Tout du plan Débutant", check: true },
        { text: "Générateur de Description HTML", check: true },
        { text: "Photo Insights (Vision IA)", check: true, highlight: true },
        { text: "Analyse concurrentielle (ASIN)", check: true },
        { text: "Support Prioritaire", check: true },
        { text: "Monkey Roast", check: false },
      ],
    },
    {
      title: "Empire",
      description: "Pour les agences et les gros volumes.",
      price: isAnnual ? empireAnnual : empireMonthly,
      annualPrice: empireMonthly,
      planKeyMain: isAnnual ? "empire_annual" : "empire_monthly",
      planKeyCrossed: "empire_monthly",
      icon: FaCrown,
      accentColor: accentColor,
      features: [
        { text: "2500 Crédits / mois", bold: true, check: true },
        { text: "Tout du plan Confirmé", check: true },
        { text: "Accès Modèle IA 'Agent' (Reasoning)", check: true },
        { text: "Photo Insights Illimité", check: true },
        { text: "Monkey Roast (Audit Expert)", check: true, highlight: true, icon: FaFire },
        { text: "Export Fichier Amazon FBA", check: true },
        { text: "Support Dédié", check: true },
      ],
    },
  ];

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={8}>
        {cardProps.map((card) => (
          <Box
            key={card.title}
            bg="gray.900"
            borderRadius="xl"
            border={card.isPopular ? "1px solid" : "none"}
            borderColor={card.isPopular ? card.accentColor : undefined}
            p={6}
            position="relative"
          >
            {card.isPopular && (
              <Badge
                bg={card.accentColor}
                color="gray.900"
                fontWeight="bold"
                fontSize="xs"
                position="absolute"
                top={2}
                right={2}
              >
                POPULAIRE
              </Badge>
            )}
            <Stack spacing={4} pt={card.isPopular ? 2 : 0}>
              <Flex justify="space-between" align="start">
                <Box>
                  <Heading size="md" color="white">{card.title}</Heading>
                  <Text fontSize="sm" color="gray.400">{card.description}</Text>
                </Box>
                <Box as={card.icon} w={8} h={8} color={card.accentColor} />
              </Flex>
              <Box>
                <Flex align="baseline">
                  <EditablePrice planKey={card.planKeyMain} amount={card.price} fontSize="4xl" />
                  <Text fontSize="lg" color="gray.400" ml={1}>/mois</Text>
                </Flex>
                {isAnnual && (
                  <Box fontSize="sm" color="gray.500" textDecoration="line-through" mt={1}>
                    <EditablePrice planKey={card.planKeyCrossed} amount={card.annualPrice} fontSize="sm" /> /mois facturé mensuellement
                  </Box>
                )}
                <Text fontSize="xs" color={card.accentColor} mt={2} fontWeight="bold">
                  {isAnnual ? `Facturé ${Math.round(card.price) * 12}€ par an` : "Sans engagement"}
                </Text>
              </Box>
              <Button size="md" colorScheme="teal" variant={card.isPopular ? "solid" : "outline"} width="100%">
                {card.isPopular ? "Choisir ce plan" : "Commencer"}
              </Button>
              <Divider borderColor="gray.600" />
              <List spacing={2}>
                {card.features.map((f, i) => (
                  <ListItem key={i} display="flex" alignItems="center" color={f.check ? "gray.200" : "gray.500"}>
                    <ListIcon as={f.check ? FaCheckCircle : FaTimesCircle} color={f.check ? "green.400" : "gray.500"} />
                    <Text fontSize="sm" fontWeight={f.bold ? "bold" : "normal"}>
                      {f.text}
                    </Text>
                  </ListItem>
                ))}
              </List>
            </Stack>
          </Box>
        ))}
      </SimpleGrid>
      <Box bg="gray.900" borderRadius="xl" p={6} border="1px solid" borderColor="gray.700">
        <Flex direction={{ base: "column", md: "row" }} align="center" justify="space-between" gap={6}>
          <Box>
            <Flex align="center" mb={2}>
              <Box as={BsStars} color="teal.400" w={6} h={6} mr={2} />
              <Heading size="md" color="white">{t("needFlexibility")}</Heading>
            </Flex>
            <Text color="gray.400" fontSize="sm">{t("payAsYouGoDescInactive")}</Text>
          </Box>
          <Box textAlign="center" minW="200px">
            <Text fontSize="sm" color="gray.500" mb={1}>{t("startingFrom")}</Text>
            <EditablePrice planKey="pay-as-you-go" amount={payAsYouGo} fontSize="3xl" />
            <Button size="sm" colorScheme="teal" variant="outline" mt={2}>
              {t("buyCredits")}
            </Button>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}

interface ProjectData {
    id: string;
    userId: string;
    name: string;
    userEmail: string;
    asin?: string;
    marketplace?: string;
    createdAt?: string;
    updatedAt?: string;
    images?: string[];
    data?: {
        images?: string[];
        feature_bullets?: string[];
        description?: string;
        title?: string;
    };
    generatedBullets?: any;
}

const Admin: React.FC = () => {
  const { getToken } = useAuth();
  const { startImpersonation } = useImpersonation();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserData[]>([]);
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Edit Modal State
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const [editRole, setEditRole] = useState("");
  const [editCredits, setEditCredits] = useState(0);
  const [editPlan, setEditPlan] = useState("");

  // Delete Modal State
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  // Inline Edit State (Projects)
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);

  // Credit Costs (Admin tab)
  const [creditCosts, setCreditCosts] = useState<Record<string, number> | null>(null);
  const [creditCostsLoading, setCreditCostsLoading] = useState(false);
  const [creditCostsSaving, setCreditCostsSaving] = useState(false);
  const [creditCostsEdit, setCreditCostsEdit] = useState<Record<string, number>>({});
  const CREDIT_KEYS = [
    "generate_bullets",
    "audit",
    "strategic_analysis",
    "roast",
    "chat_message",
    "assertions",
    "analyze_listing",
    "regenerate_bullet",
    "translate",
  ] as const;

  // Credits per plan (Admin tab)
  const PLAN_KEYS = ["free", "starter", "pro", "enterprise"] as const;

  // Stripe Pricing (Admin tab)
  const STRIPE_PLAN_KEYS = ["starter_monthly", "starter_annual", "pro_monthly", "pro_annual", "empire_monthly", "empire_annual", "pay-as-you-go"] as const;
  const [stripePricesData, setStripePricesData] = useState<{
    config: Record<string, string>;
    prices: { id: string; amountFormatted?: string; currency?: string; recurring?: { interval: string } | null; error?: string }[];
    planKeys: string[];
  } | null>(null);
  const [stripePricesLoading, setStripePricesLoading] = useState(false);
  const [stripeReimportLoading, setStripeReimportLoading] = useState(false);
  const [stripePriceUpdating, setStripePriceUpdating] = useState<string | null>(null);
  const [stripeNewAmountMap, setStripeNewAmountMap] = useState<Record<string, string>>({});
  const [isAnnualPreview, setIsAnnualPreview] = useState(true);
  const DEFAULT_CREDITS_PER_PLAN: Record<string, number> = {
    free: 100,
    starter: 400,
    pro: 1500,
    enterprise: 10000,
  };
  const [creditsPerPlan, setCreditsPerPlan] = useState<Record<string, number> | null>(null);
  const [creditsPerPlanLoading, setCreditsPerPlanLoading] = useState(false);
  const [creditsPerPlanSaving, setCreditsPerPlanSaving] = useState(false);
  const [creditsPerPlanEdit, setCreditsPerPlanEdit] = useState<Record<string, number>>({});

  // Coupons
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [newCouponCode, setNewCouponCode] = useState("");
  const [newCouponCredits, setNewCouponCredits] = useState(0);
  const [newCouponPlan, setNewCouponPlan] = useState("");
  const [newCouponRepeatable, setNewCouponRepeatable] = useState(false);
  const [creatingCoupon, setCreatingCoupon] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/adminGetUsers", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok) {
        setUsers(data.users);
      } else {
        throw new Error(data.error || "Failed to fetch users");
      }
    } catch (error: any) {
      toast({
        title: "Error fetching users",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/adminGetAllProjects", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok) {
        setProjects(data.projects);
      } else {
        throw new Error(data.error || "Failed to fetch projects");
      }
    } catch (error: any) {
      toast({
        title: "Error fetching projects",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProjectsLoading(false);
    }
  };

  const fetchCoupons = async () => {
      setCouponsLoading(true);
      try {
          const token = await getToken();
          const response = await fetch("/.netlify/functions/adminGetCoupons", {
              headers: { Authorization: `Bearer ${token}` }
          });
          const data = await response.json();
          if (response.ok) {
              setCoupons(data.coupons);
          } else {
              throw new Error(data.error || "Failed to fetch coupons");
          }
      } catch (error: any) {
          toast({ title: "Error", description: error.message, status: "error" });
      } finally {
          setCouponsLoading(false);
      }
  };

  const handleTabChange = (index: number) => {
      if (index === 1 && projects.length === 0) {
          fetchProjects();
      }
      if (index === 2) {
          if (creditCosts === null) fetchCreditCosts();
          if (creditsPerPlan === null) fetchCreditsPerPlan();
      }
      if (index === 3 && stripePricesData === null) {
          fetchStripePrices();
      }
      if (index === 4 && coupons.length === 0) {
          fetchCoupons();
      }
  };

  const handleCreateCoupon = async () => {
      if (!newCouponCode) return;
      setCreatingCoupon(true);
      try {
          const token = await getToken();
          const response = await fetch("/.netlify/functions/adminCreateCoupon", {
              method: "POST",
              headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                  code: newCouponCode,
                  credits: newCouponCredits,
                  plan: newCouponPlan || null,
                  repeatable: newCouponRepeatable
              })
          });
          const data = await response.json();
          if (response.ok) {
              setCoupons([...coupons, data]);
              setNewCouponCode("");
              setNewCouponCredits(0);
              setNewCouponPlan("");
              setNewCouponRepeatable(false);
              toast({ title: "Coupon created", status: "success" });
          } else {
              throw new Error(data.error || "Failed to create coupon");
          }
      } catch (error: any) {
          toast({ title: "Error", description: error.message, status: "error" });
      } finally {
          setCreatingCoupon(false);
      }
  };

  const handleDeleteCoupon = async (id: string) => {
      if (!window.confirm("Are you sure?")) return;
      try {
          const token = await getToken();
          const response = await fetch("/.netlify/functions/adminDeleteCoupon", {
              method: "DELETE",
              headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({ id })
          });
          if (response.ok) {
              setCoupons(coupons.filter(c => c.id !== id));
              toast({ title: "Coupon deleted", status: "success" });
          } else {
              const data = await response.json();
              throw new Error(data.error || "Failed to delete coupon");
          }
      } catch (error: any) {
          toast({ title: "Error", description: error.message, status: "error" });
      }
  };

  // ... (keeping existing fetch functions for Stripe, Credits etc)
  const fetchStripePrices = async () => {
    setStripePricesLoading(true);
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/adminStripePrices", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to fetch Stripe prices");
      setStripePricesData(data);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, status: "error" });
    } finally {
      setStripePricesLoading(false);
    }
  };

  const handleReimportStripePrices = async () => {
    setStripeReimportLoading(true);
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/adminStripePrices", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ resetToDefaults: true }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to reimport Stripe prices");
      toast({ title: t("adminStripePricesReimportSuccess"), status: "success" });
      fetchStripePrices();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, status: "error" });
    } finally {
      setStripeReimportLoading(false);
    }
  };

  const handleStripePriceUpdate = async (planKey: string, amountEurosArg?: number) => {
    const amountEuros =
      amountEurosArg ??
      (() => {
        const rawVal = stripeNewAmountMap[planKey];
        if (rawVal == null || rawVal === "") {
          toast({ title: "Invalid", description: "Enter new amount in euros", status: "warning" });
          return NaN;
        }
        return parseFloat(rawVal.replace(",", "."));
      })();
    if (isNaN(amountEuros) || amountEuros < 0) {
      toast({ title: "Invalid", description: "Amount must be a positive number", status: "warning" });
      return;
    }
    const cents = Math.round(amountEuros * 100);
    setStripePriceUpdating(planKey);
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/adminStripeUpdatePrice", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ planKey, newUnitAmountCents: cents, currency: "eur", archiveOldPrice: true }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to update price");
      toast({
        title: t("adminStripePriceUpdatedTitle"),
        description: t("adminStripePriceUpdatedDesc", { planKey, amount: amountEuros }),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setStripeNewAmountMap((prev) => ({ ...prev, [planKey]: "" }));
      fetchStripePrices();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, status: "error" });
    } finally {
      setStripePriceUpdating(null);
    }
  };

  const fetchCreditCosts = async () => {
    setCreditCostsLoading(true);
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/adminCreditCosts", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to fetch credit costs");
      setCreditCosts(data);
      setCreditCostsEdit(data || {});
    } catch (error: any) {
      toast({ title: "Error", description: error.message, status: "error" });
    } finally {
      setCreditCostsLoading(false);
    }
  };

  const handleCreditCostChange = (key: string, value: string) => {
    const n = parseInt(value, 10);
    if (value === "" || (Number.isInteger(n) && n >= 0)) {
      setCreditCostsEdit((prev) => ({ ...prev, [key]: value === "" ? 0 : n }));
    }
  };

  const fetchCreditsPerPlan = async () => {
    setCreditsPerPlanLoading(true);
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/adminCreditsPerPlan", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setCreditsPerPlan(data);
        setCreditsPerPlanEdit(data || {});
      } else {
        setCreditsPerPlan(DEFAULT_CREDITS_PER_PLAN);
        setCreditsPerPlanEdit(DEFAULT_CREDITS_PER_PLAN);
        toast({ title: "Credits per plan: using defaults (API may be unavailable)", status: "info", duration: 5000 });
      }
    } catch (error: any) {
      setCreditsPerPlan(DEFAULT_CREDITS_PER_PLAN);
      setCreditsPerPlanEdit(DEFAULT_CREDITS_PER_PLAN);
      toast({ title: "Error fetching credits per plan", description: error.message, status: "error" });
    } finally {
      setCreditsPerPlanLoading(false);
    }
  };

  const handleCreditsPerPlanChange = (key: string, value: string) => {
    const n = parseInt(value, 10);
    if (value === "" || (Number.isInteger(n) && n >= 0)) {
      setCreditsPerPlanEdit((prev) => ({ ...prev, [key]: value === "" ? 0 : n }));
    }
  };

  const handleSaveCreditsPerPlan = async () => {
    setCreditsPerPlanSaving(true);
    try {
      const token = await getToken();
      const updates: Record<string, number> = {};
      for (const key of PLAN_KEYS) {
        const val = creditsPerPlanEdit[key] ?? creditsPerPlan?.[key] ?? DEFAULT_CREDITS_PER_PLAN[key] ?? 0;
        if (!Number.isInteger(val) || val < 0) {
          toast({ title: "Invalid value", description: `${key} must be a non-negative integer`, status: "error" });
          setCreditsPerPlanSaving(false);
          return;
        }
        updates[key] = val;
      }
      const response = await fetch("/.netlify/functions/adminCreditsPerPlan", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to update credits per plan");
      setCreditsPerPlan(data);
      setCreditsPerPlanEdit(data);
      toast({ title: "Credits per plan updated", status: "success" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, status: "error" });
    } finally {
      setCreditsPerPlanSaving(false);
    }
  };

  const handleSaveCreditCosts = async () => {
    setCreditCostsSaving(true);
    try {
      const token = await getToken();
      const updates: Record<string, number> = {};
      for (const key of CREDIT_KEYS) {
        const val = creditCostsEdit[key] ?? creditCosts?.[key] ?? 0;
        if (!Number.isInteger(val) || val < 0) {
          toast({ title: "Invalid value", description: `${key} must be a non-negative integer`, status: "error" });
          setCreditCostsSaving(false);
          return;
        }
        updates[key] = val;
      }
      const response = await fetch("/.netlify/functions/adminCreditCosts", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to update credit costs");
      setCreditCosts(data);
      setCreditCostsEdit(data);
      toast({ title: "Credit costs updated", status: "success" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, status: "error" });
    } finally {
      setCreditCostsSaving(false);
    }
  };

  const formatDate = (dateString?: string) => {
      if (!dateString) return "-";
      return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const handleEditClick = (user: UserData) => {
    setSelectedUser(user);
    setEditRole(user.role || "user");
    setEditCredits(user.credits || 0);
    setEditPlan(user.plan || "free");
    onEditOpen();
  };

  const handleDeleteClick = (user: UserData) => {
    setSelectedUser(user);
    onDeleteOpen();
  };

  const handleImpersonateClick = async (user: UserData) => {
      if (!window.confirm(`Are you sure you want to login as ${user.email}? You will be logged out of your admin account.`)) {
          return;
      }

      try {
          const token = await getToken();
          
          const response = await fetch("/.netlify/functions/adminImpersonateUser", {
              method: "POST",
              headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                  targetUserId: user.id
              })
          });

          if (!response.ok) {
              const data = await response.json();
              throw new Error(data.error || "Failed to impersonate user");
          }

          const { customToken } = await response.json();

          // Démarrer le mode impersonation (sauvegarder l'état)
          startImpersonation("active"); // On stocke juste un marqueur "active" car on ne peut pas vraiment stocker le token admin de manière perenne sécurisée sans refresh token. L'user devra se relogguer.

          // Sign in with the custom token
          await signInWithCustomToken(auth, customToken);
          
          toast({
              title: `Logged in as ${user.email}`,
              status: "success",
              duration: 3000
          });

          navigate("/dashboard");
          
      } catch (error: any) {
          console.error("Impersonation error", error);
          toast({
              title: "Impersonation failed",
              description: error.message,
              status: "error",
              duration: 5000
          });
      }
  };

  const handleViewProject = async (project: ProjectData) => {
      if (!window.confirm(`Viewing this project requires logging in as ${project.userEmail}. Continue?`)) {
          return;
      }

      try {
          const token = await getToken();
          
          const response = await fetch("/.netlify/functions/adminImpersonateUser", {
              method: "POST",
              headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                  targetUserId: project.userId
              })
          });

          if (!response.ok) {
              const data = await response.json();
              throw new Error(data.error || "Failed to access project");
          }

          const { customToken } = await response.json();

          startImpersonation("active");
          await signInWithCustomToken(auth, customToken);
          
          navigate(`/projects/${project.id}`);
          
      } catch (error: any) {
          console.error("Project access error", error);
          toast({
              title: "Failed to access project",
              description: error.message,
              status: "error",
              duration: 5000
          });
      }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/adminUpdateUser", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
            targetUserId: selectedUser.id,
            updates: {
                role: editRole,
                credits: Number(editCredits),
                plan: editPlan
            }
        }),
      });

      if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || "Failed to update user");
      }

      toast({
        title: "User updated",
        status: "success",
        duration: 3000,
      });
      
      onEditClose();
      fetchUsers(); // Refresh list
    } catch (error: any) {
      toast({
        title: "Error updating user",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleStartEditProject = (project: ProjectData) => {
      setEditingProjectId(project.id);
      setEditingName(project.name || "");
  };

  const handleCancelEditProject = () => {
      setEditingProjectId(null);
      setEditingName("");
  };

  const handleSaveEditProject = async (projectId: string) => {
      if (!projectId) return;
      setIsSavingName(true);

      try {
          const token = await getToken();
          const response = await fetch("/.netlify/functions/adminUpdateProject", {
              method: "POST",
              headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                  projectId: projectId,
                  updates: {
                      name: editingName
                  }
              })
          });

          if (!response.ok) {
              const data = await response.json();
              throw new Error(data.error || "Failed to update project");
          }

          toast({
              title: "Project name updated",
              status: "success",
              duration: 2000
          });

          // Mettre à jour la liste locale pour éviter un refresh complet
          setProjects(prev => prev.map(p => 
              p.id === projectId ? { ...p, name: editingName } : p
          ));

          setEditingProjectId(null);
          
      } catch (error: any) {
          toast({
              title: "Error updating project",
              description: error.message,
              status: "error",
              duration: 5000
          });
      } finally {
          setIsSavingName(false);
      }
  };

  const handleDeleteUser = async () => {
     if (!selectedUser) return;

    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/adminDeleteUser", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
            targetUserId: selectedUser.id,
        }),
      });

      if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || "Failed to delete user");
      }

      toast({
        title: "User deleted",
        status: "success",
        duration: 3000,
      });
      
      onDeleteClose();
      fetchUsers(); // Refresh list
    } catch (error: any) {
      toast({
        title: "Error deleting user",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const calculateProgress = (project: ProjectData) => {
      let score = 0;
      let total = 4; // 4 critères principaux

      // 1. Titre / Info de base
      if (project.name || project.data?.title) score++;

      // 2. Images (soit dans data.images importé, soit dans images uploadé)
      const hasImages = (project.images && project.images.length > 0) || (project.data?.images && project.data.images.length > 0);
      if (hasImages) score++;

      // 3. Bullets (soit importés, soit générés)
      const hasBullets = (project.data?.feature_bullets && project.data.feature_bullets.length > 0) || project.generatedBullets;
      if (hasBullets) score++;

      // 4. Description
      if (project.data?.description && project.data.description.length > 20) score++;

      return (score / total) * 100;
  };

  return (
    <Box p={8}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading>Administration Dashboard</Heading>
      </Flex>
      
      <Tabs variant='soft-rounded' colorScheme='teal' onChange={handleTabChange}>
        <TabList mb={4}>
            <Tab>Users</Tab>
            <Tab>Projects (Recent)</Tab>
            <Tab>Credits / Costs</Tab>
            <Tab>{t("adminPricing")}</Tab>
            <Tab>Coupons</Tab>
        </TabList>

        <TabPanels>
            {/* USERS TAB */}
            <TabPanel p={0}>
                {loading ? (
                     <Flex justify="center" align="center" h="30vh"><Spinner size="xl" /></Flex>
                ) : (
                <Box overflowX="auto" bg="gray.800" borderRadius="md" p={4}>
                    <Flex justify="flex-end" mb={4}>
                        <Button onClick={fetchUsers} size="xs" leftIcon={<ViewIcon/>}>Refresh Users</Button>
                    </Flex>
                    <Table variant="simple" size="sm" sx={{ tableLayout: "fixed" }}>
                    <Thead>
                        <Tr>
                        <Th color="gray.400" width="30%">User</Th>
                        <Th color="gray.400" width="25%">Activity</Th>
                        <Th color="gray.400" width="15%">Stats</Th>
                        <Th color="gray.400" width="15%">Plan</Th>
                        <Th color="gray.400" width="15%">Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {users.map((user) => (
                        <Tr key={user.id}>
                            <Td>
                                <Box>
                                    <Tooltip label={user.email}>
                                        <Text fontWeight="bold" isTruncated>{user.email}</Text>
                                    </Tooltip>
                                    <Text fontSize="xs" color="gray.500" isTruncated>{user.companyName}</Text>
                                    <Badge mt={1} colorScheme={user.role === 'admin' ? 'purple' : 'gray'}>
                                        {user.role || 'user'}
                                    </Badge>
                                </Box>
                            </Td>
                            <Td fontSize="xs">
                                <Box>Created: {formatDate(user.authCreationTime)}</Box>
                                <Box color={user.lastSignInTime ? "green.300" : "gray.500"}>
                                    Last Login: {formatDate(user.lastSignInTime)}
                                </Box>
                            </Td>
                            <Td>
                                <Box>Credits: <b>{user.credits}</b></Box>
                                <Box>Projects: <b>{user.projectCount || 0}</b></Box>
                            </Td>
                            <Td>
                                <Badge colorScheme={user.plan === 'pro' ? 'gold' : 'blue'}>
                                    {user.plan || 'free'}
                                </Badge>
                            </Td>
                            <Td>
                            <Menu>
                                <MenuButton as={IconButton} icon={<SettingsIcon />} size="xs" variant="ghost" />
                                <MenuList>
                                    <MenuItem icon={<ViewIcon />} onClick={() => handleImpersonateClick(user)}>
                                        Login as User
                                    </MenuItem>
                                <MenuItem icon={<EditIcon />} onClick={() => handleEditClick(user)}>
                                    Edit Details
                                </MenuItem>
                                <MenuItem icon={<DeleteIcon />} color="red.400" onClick={() => handleDeleteClick(user)}>
                                    Delete User
                                </MenuItem>
                                </MenuList>
                            </Menu>
                            </Td>
                        </Tr>
                        ))}
                    </Tbody>
                    </Table>
                </Box>
                )}
            </TabPanel>

            {/* PROJECTS TAB */}
            <TabPanel p={0}>
                {projectsLoading ? (
                     <Flex justify="center" align="center" h="30vh"><Spinner size="xl" /></Flex>
                ) : (
                    <Box overflowX="auto" bg="gray.800" borderRadius="md" p={4}>
                         <Flex justify="space-between" mb={4} gap={4}>
                            <Input 
                                placeholder="Rechercher par nom, email, ASIN, description ou bullet..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                maxW="400px"
                                size="sm"
                                borderRadius="md"
                            />
                            <Button onClick={fetchProjects} size="xs" leftIcon={<ViewIcon/>}>Refresh Projects</Button>
                        </Flex>
                        <Table variant="simple" size="sm" sx={{ tableLayout: "fixed" }}>
                        <Thead>
                            <Tr>
                            <Th color="gray.400" width="35%">Project</Th>
                            <Th color="gray.400" width="25%">Owner</Th>
                            <Th color="gray.400" width="10%">Mode</Th>
                            <Th color="gray.400" width="15%">Progress</Th>
                            <Th color="gray.400" width="15%">Dates</Th>
                            <Th color="gray.400" width="10%">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {projects
                                .filter(project => {
                                    const query = searchQuery.toLowerCase();
                                    
                                    // Recherche dans les bullets (tableau de strings)
                                    const bulletsMatch = project.data?.feature_bullets?.some(bullet => 
                                        bullet.toLowerCase().includes(query)
                                    );

                                    // Recherche dans la description
                                    const descriptionMatch = project.data?.description?.toLowerCase().includes(query);

                                    return (
                                        (project.name && project.name.toLowerCase().includes(query)) ||
                                        (project.userEmail && project.userEmail.toLowerCase().includes(query)) ||
                                        (project.asin && project.asin.toLowerCase().includes(query)) ||
                                        bulletsMatch ||
                                        descriptionMatch
                                    );
                                })
                                .map((project) => (
                            <Tr key={project.id}>
                                <Td fontWeight="medium">
                                    <Flex align="center" maxW="100%">
                                        {editingProjectId === project.id ? (
                                            <Flex align="center" width="100%" mr={2}>
                                                <Input 
                                                    value={editingName} 
                                                    onChange={(e) => setEditingName(e.target.value)}
                                                    size="xs"
                                                    mr={1}
                                                    autoFocus
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleSaveEditProject(project.id);
                                                        if (e.key === 'Escape') handleCancelEditProject();
                                                    }}
                                                />
                                                <IconButton
                                                    aria-label="Save"
                                                    icon={<CheckIcon />}
                                                    size="xs"
                                                    colorScheme="green"
                                                    isLoading={isSavingName}
                                                    onClick={() => handleSaveEditProject(project.id)}
                                                    mr={1}
                                                />
                                                <IconButton
                                                    aria-label="Cancel"
                                                    icon={<CloseIcon />}
                                                    size="xs"
                                                    onClick={handleCancelEditProject}
                                                />
                                            </Flex>
                                        ) : (
                                            <Flex align="center" width="100%" role="group">
                                                <Tooltip label={project.name || "Untitled"}>
                                                    <Text isTruncated mr={2} maxW="200px">
                                                        {project.name || "Untitled"}
                                                    </Text>
                                                </Tooltip>
                                                <IconButton 
                                                    aria-label="Edit name" 
                                                    icon={<EditIcon />} 
                                                    size="xs" 
                                                    variant="ghost" 
                                                    color="gray.500"
                                                    opacity={0}
                                                    _groupHover={{ opacity: 1 }}
                                                    onClick={() => handleStartEditProject(project)}
                                                />
                                                {project.asin && <Badge ml={2} flexShrink={0} variant="outline" colorScheme="orange">{project.asin}</Badge>}
                                            </Flex>
                                        )}
                                    </Flex>
                                    <Badge size="sm" variant="subtle" mt={1}>{project.marketplace || "-"}</Badge>
                                </Td>
                                <Td>
                                    <Tooltip label={project.userEmail}>
                                        <Text isTruncated>{project.userEmail}</Text>
                                    </Tooltip>
                                </Td>
                                <Td>
                                    <Badge colorScheme={project.asin ? 'purple' : 'green'}>
                                        {project.asin ? 'Analyze' : 'Creation'}
                                    </Badge>
                                </Td>
                                <Td>
                                    <Tooltip label={`${calculateProgress(project)}% Complete`}>
                                        <Progress value={calculateProgress(project)} size="sm" colorScheme={calculateProgress(project) === 100 ? "green" : "blue"} borderRadius="full" />
                                    </Tooltip>
                                </Td>
                                <Td fontSize="xs">
                                    <Box isTruncated>Created: {formatDate(project.createdAt).split(' ')[0]}</Box>
                                    <Box color="gray.500" isTruncated>Updated: {formatDate(project.updatedAt).split(' ')[0]}</Box>
                                </Td>
                                <Td>
                                    <Tooltip label="View Project (Log in as User)">
                                        <IconButton 
                                            icon={<ExternalLinkIcon />} 
                                            size="sm" 
                                            aria-label="View Project" 
                                            onClick={() => handleViewProject(project)}
                                            colorScheme="teal"
                                            variant="ghost"
                                        />
                                    </Tooltip>
                                </Td>
                            </Tr>
                            ))}
                        </Tbody>
                        </Table>
                    </Box>
                )}
            </TabPanel>

            {/* CREDITS / COSTS TAB */}
            <TabPanel p={0}>
                {creditCostsLoading || creditsPerPlanLoading ? (
                  <Flex justify="center" align="center" h="30vh"><Spinner size="xl" /></Flex>
                ) : (
                  <Box bg="gray.800" borderRadius="md" p={6}>
                    {/* Credit costs per action */}
                    <Flex justify="space-between" align="center" mb={6}>
                      <Heading size="md" color="white">Credit costs per action</Heading>
                      <Flex gap={2}>
                        <Button size="sm" leftIcon={<ViewIcon />} onClick={fetchCreditCosts} variant="outline">
                          Refresh
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="teal"
                          onClick={handleSaveCreditCosts}
                          isLoading={creditCostsSaving}
                          isDisabled={creditCostsSaving || creditCosts === null}
                        >
                          Save changes
                        </Button>
                      </Flex>
                    </Flex>
                    <Table variant="simple" size="sm" mb={8}>
                      <Thead>
                        <Tr>
                          <Th color="gray.400">Action</Th>
                          <Th color="gray.400" w="120px">Cost (credits)</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {CREDIT_KEYS.map((key) => (
                          <Tr key={key}>
                            <Td>
                              <Text fontFamily="mono" fontSize="sm">{key}</Text>
                            </Td>
                            <Td>
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                value={creditCostsEdit[key] ?? creditCosts?.[key] ?? 0}
                                onChange={(e) => handleCreditCostChange(key, e.target.value)}
                                size="sm"
                                w="100px"
                                bg="gray.900"
                                borderColor="gray.600"
                              />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>

                    <Divider borderColor="gray.600" my={6} />

                    {/* Credits per plan */}
                    <Flex justify="space-between" align="center" mb={4}>
                      <Heading size="md" color="white">Credits per plan</Heading>
                      <Flex gap={2}>
                        <Button size="sm" leftIcon={<ViewIcon />} onClick={fetchCreditsPerPlan} variant="outline">
                          Refresh
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="teal"
                          onClick={handleSaveCreditsPerPlan}
                          isLoading={creditsPerPlanSaving}
                          isDisabled={creditsPerPlanSaving || creditsPerPlan === null}
                        >
                          Save changes
                        </Button>
                      </Flex>
                    </Flex>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th color="gray.400">Plan</Th>
                          <Th color="gray.400" w="120px">Credits</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {PLAN_KEYS.map((key) => (
                          <Tr key={key}>
                            <Td>
                              <Text fontFamily="mono" fontSize="sm">{key}</Text>
                            </Td>
                            <Td>
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                value={creditsPerPlanEdit[key] ?? creditsPerPlan?.[key] ?? DEFAULT_CREDITS_PER_PLAN[key] ?? 0}
                                onChange={(e) => handleCreditsPerPlanChange(key, e.target.value)}
                                size="sm"
                                w="100px"
                                bg="gray.900"
                                borderColor="gray.600"
                              />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>

                    <Divider borderColor="gray.600" my={6} />

                    {/* Preview: actions per plan per month */}
                    <Heading size="sm" color="white" mb={4}>
                      Preview: actions per plan per month
                    </Heading>
                    <Text fontSize="xs" color="gray.500" mb={4}>
                      Nombre d&apos;actions possibles selon les crédits du plan et les coûts ci-dessus (sans enregistrer).
                    </Text>
                    <Box overflowX="auto">
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th color="gray.400">Action</Th>
                            {PLAN_KEYS.map((plan) => (
                              <Th key={plan} color="gray.400" textAlign="center" minW="80px">
                                {plan}
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {CREDIT_KEYS.map((actionKey) => {
                            const cost = creditCostsEdit[actionKey] ?? creditCosts?.[actionKey] ?? 0;
                            return (
                              <Tr key={actionKey}>
                                <Td>
                                  <Text fontFamily="mono" fontSize="sm">{actionKey}</Text>
                                </Td>
                                {PLAN_KEYS.map((planKey) => {
                                  const credits = creditsPerPlanEdit[planKey] ?? creditsPerPlan?.[planKey] ?? DEFAULT_CREDITS_PER_PLAN[planKey] ?? 0;
                                  const count = cost > 0 ? Math.floor(Number(credits) / cost) : "∞";
                                  return (
                                    <Td key={planKey} textAlign="center">
                                      <Text fontFamily="mono" fontSize="sm">{count}</Text>
                                    </Td>
                                  );
                                })}
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                    <Text fontSize="xs" color="gray.500" mt={4}>
                      Changes are validated (non-negative integers only). The backend enforces admin role.
                    </Text>
                  </Box>
                )}
            </TabPanel>

            {/* PRICING TAB */}
            <TabPanel p={0}>
                {stripePricesLoading ? (
                  <Flex justify="center" align="center" h="30vh"><Spinner size="xl" /></Flex>
                ) : (
                  <Box bg="gray.800" borderRadius="md" p={6}>
                    <Flex justify="space-between" align="center" mb={6}>
                      <Heading size="md" color="white">{t("adminStripePrices")}</Heading>
                      <Flex gap={2}>
                        <Button size="sm" leftIcon={<ViewIcon />} onClick={fetchStripePrices} variant="outline">
                          {t("refresh")}
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="teal"
                          variant="outline"
                          onClick={handleReimportStripePrices}
                          isLoading={stripeReimportLoading}
                        >
                          {t("adminStripePricesReimport")}
                        </Button>
                      </Flex>
                    </Flex>
                    <Text fontSize="sm" color="gray.400" mb={4}>
                      {t("adminStripePricesDesc")}
                    </Text>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th color="gray.400">{t("adminPlanKey")}</Th>
                          <Th color="gray.400">{t("adminCurrentPrice")}</Th>
                          <Th color="gray.400">Price ID</Th>
                          <Th color="gray.400" w="180px">{t("adminNewPrice")}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stripePricesData?.planKeys?.map((planKey) => {
                          const priceId = stripePricesData.config?.[planKey];
                          const priceDetail = stripePricesData.prices?.find((p) => p.id === priceId);
                          const amountFormatted = priceDetail?.amountFormatted ?? priceDetail?.error ?? "-";
                          const interval = priceDetail?.recurring?.interval;
                          return (
                            <Tr key={planKey}>
                              <Td>
                                <Text fontFamily="mono" fontSize="sm">{planKey}</Text>
                                {interval && (
                                  <Badge size="sm" variant="subtle" colorScheme="blue" ml={1}>{interval}</Badge>
                                )}
                              </Td>
                              <Td>
                                <Text fontWeight="medium">{amountFormatted}</Text>
                                {priceDetail?.currency && (
                                  <Text fontSize="xs" color="gray.500">{priceDetail.currency}</Text>
                                )}
                              </Td>
                              <Td>
                                <Text fontFamily="mono" fontSize="xs" color="gray.400" isTruncated maxW="200px">{priceId || "-"}</Text>
                              </Td>
                              <Td>
                                <Flex align="center" gap={2}>
                                  <Input
                                    placeholder="€"
                                    value={stripeNewAmountMap[planKey] ?? ""}
                                    onChange={(e) => setStripeNewAmountMap((prev) => ({ ...prev, [planKey]: e.target.value }))}
                                    size="sm"
                                    w="80px"
                                    bg="gray.900"
                                    borderColor="gray.600"
                                    onKeyDown={(e) => e.key === "Enter" && handleStripePriceUpdate(planKey)}
                                  />
                                  <Button
                                    size="sm"
                                    colorScheme="teal"
                                    onClick={() => handleStripePriceUpdate(planKey)}
                                    isLoading={stripePriceUpdating === planKey}
                                    isDisabled={!stripeNewAmountMap[planKey]?.trim()}
                                  >
                                    {t("update")}
                                  </Button>
                                </Flex>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                    <Text fontSize="xs" color="gray.500" mt={4}>
                      {t("adminStripePricesHint")}
                    </Text>

                    <Divider borderColor="gray.600" my={8} />

                    {/* Live Preview - similar to public Pricing page */}
                    <Heading size="md" color="white" mb={4}>
                      {t("adminPricingLivePreview")}
                    </Heading>
                    <Text fontSize="sm" color="gray.400" mb={6}>
                      {t("adminPricingLivePreviewDesc")}
                    </Text>
                    <Flex justify="center" align="center" gap={4} mb={8}>
                      <Text
                        fontWeight="bold"
                        color={!isAnnualPreview ? "white" : "gray.400"}
                        cursor="pointer"
                        onClick={() => setIsAnnualPreview(false)}
                      >
                        {t("monthly")}
                      </Text>
                      <Switch
                        size="lg"
                        colorScheme="teal"
                        isChecked={isAnnualPreview}
                        onChange={() => setIsAnnualPreview(!isAnnualPreview)}
                      />
                      <Text
                        fontWeight="bold"
                        color={isAnnualPreview ? "white" : "gray.400"}
                        cursor="pointer"
                        onClick={() => setIsAnnualPreview(true)}
                      >
                        {t("annual")} <Badge ml={2} colorScheme="teal">-40%</Badge>
                      </Text>
                    </Flex>
                    <AdminPricingPreview
                      stripePricesData={stripePricesData}
                      isAnnual={isAnnualPreview}
                      t={t}
                      onPriceUpdate={async (planKey, amountEuros) =>
                        handleStripePriceUpdate(planKey, amountEuros)
                      }
                      updatingPlanKey={stripePriceUpdating}
                    />
                  </Box>
                )}
            </TabPanel>

            {/* COUPONS TAB */}
            <TabPanel p={0}>
                {couponsLoading ? (
                    <Flex justify="center" align="center" h="30vh"><Spinner size="xl" /></Flex>
                ) : (
                    <Box bg="gray.800" borderRadius="md" p={6}>
                        <Flex justify="space-between" align="center" mb={6}>
                            <Heading size="md" color="white">Coupons Management</Heading>
                            <Button size="sm" onClick={fetchCoupons} leftIcon={<ViewIcon/>}>Refresh</Button>
                        </Flex>

                        {/* Create Coupon Form */}
                        <Box bg="gray.700" p={4} borderRadius="md" mb={8}>
                            <Heading size="sm" mb={4}>Create New Coupon</Heading>
                            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} alignItems="end">
                                <FormControl>
                                    <FormLabel fontSize="xs">Code</FormLabel>
                                    <Input 
                                        placeholder="PROMO2025" 
                                        value={newCouponCode}
                                        onChange={(e) => setNewCouponCode(e.target.value.toUpperCase())}
                                        bg="gray.800"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="xs">Credits Reward</FormLabel>
                                    <Input 
                                        type="number"
                                        placeholder="0"
                                        value={newCouponCredits}
                                        onChange={(e) => setNewCouponCredits(Number(e.target.value))}
                                        bg="gray.800"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="xs">Plan Upgrade (Optional)</FormLabel>
                                    <Select 
                                        placeholder="No plan change" 
                                        value={newCouponPlan}
                                        onChange={(e) => setNewCouponPlan(e.target.value)}
                                        bg="gray.800"
                                    >
                                        <option value="starter">Starter</option>
                                        <option value="pro">Pro</option>
                                        <option value="empire">Empire</option>
                                    </Select>
                                </FormControl>
                                <Flex align="center" gap={4}>
                                    <Checkbox 
                                        isChecked={newCouponRepeatable}
                                        onChange={(e) => setNewCouponRepeatable(e.target.checked)}
                                    >
                                        Repeatable
                                    </Checkbox>
                                    <Button 
                                        colorScheme="green" 
                                        leftIcon={<AddIcon />} 
                                        onClick={handleCreateCoupon}
                                        isLoading={creatingCoupon}
                                        isDisabled={!newCouponCode}
                                        width="100%"
                                    >
                                        Create
                                    </Button>
                                </Flex>
                            </SimpleGrid>
                        </Box>

                        {/* Coupons List */}
                        <Table variant="simple" size="sm">
                            <Thead>
                                <Tr>
                                    <Th color="gray.400">Code</Th>
                                    <Th color="gray.400">Reward</Th>
                                    <Th color="gray.400">Type</Th>
                                    <Th color="gray.400">Created</Th>
                                    <Th color="gray.400">Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {coupons.map(coupon => (
                                    <Tr key={coupon.id}>
                                        <Td>
                                            <Badge colorScheme="purple" fontSize="md">{coupon.code}</Badge>
                                        </Td>
                                        <Td>
                                            <Stack spacing={1}>
                                                {coupon.credits > 0 && (
                                                    <Flex align="center" gap={2}>
                                                        <Icon as={BsStars} color="yellow.400" />
                                                        <Text>+{coupon.credits} credits</Text>
                                                    </Flex>
                                                )}
                                                {coupon.plan && (
                                                    <Flex align="center" gap={2}>
                                                        <Icon as={FaCrown} color="orange.400" />
                                                        <Text>Upgrade to <b>{coupon.plan}</b></Text>
                                                    </Flex>
                                                )}
                                                {!coupon.credits && !coupon.plan && (
                                                    <Text color="gray.500">No effect</Text>
                                                )}
                                            </Stack>
                                        </Td>
                                        <Td>
                                            {coupon.repeatable ? (
                                                <Badge colorScheme="blue">Repeatable</Badge>
                                            ) : (
                                                <Badge colorScheme="gray">One-time</Badge>
                                            )}
                                        </Td>
                                        <Td fontSize="xs" color="gray.500">
                                            {formatDate(coupon.createdAt)}
                                        </Td>
                                        <Td>
                                            <IconButton 
                                                aria-label="Delete" 
                                                icon={<DeleteIcon />} 
                                                size="sm" 
                                                colorScheme="red" 
                                                variant="ghost" 
                                                onClick={() => handleDeleteCoupon(coupon.id)}
                                            />
                                        </Td>
                                    </Tr>
                                ))}
                                {coupons.length === 0 && (
                                    <Tr>
                                        <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                                            No coupons found. Create one above!
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </Box>
                )}
            </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
                <FormLabel>Role</FormLabel>
                <Select value={editRole} onChange={(e) => setEditRole(e.target.value)}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                </Select>
            </FormControl>
            <FormControl mb={4}>
                <FormLabel>Credits</FormLabel>
                <Input type="number" value={editCredits} onChange={(e) => setEditCredits(Number(e.target.value))} />
            </FormControl>
            <FormControl mb={4}>
                <FormLabel>Plan</FormLabel>
                <Select value={editPlan} onChange={(e) => setEditPlan(e.target.value)}>
                    <option value="free">Free</option>
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleUpdateUser}>
              Save
            </Button>
            <Button variant="ghost" onClick={onEditClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete user <strong>{selectedUser?.email}</strong>?
            This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={handleDeleteUser}>
              Delete
            </Button>
            <Button variant="ghost" onClick={onDeleteClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default Admin;
